import React, { useState, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { toast } from 'react-hot-toast';
import { Building2, Loader2 } from 'lucide-react';
import { api } from '../../services/api';

// Generic account interface for callback - consumers can cast as needed
interface PlaidLinkButtonProps {
  onSuccess: (accounts: any[]) => void;
  onExit?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  className?: string;
  children?: React.ReactNode;
}

/**
 * Plaid Link Button Component
 * 
 * Initiates the Plaid Link flow to connect bank accounts.
 * After successful connection, accounts are stored in the backend
 * and returned to the parent component.
 */
export default function PlaidLinkButton({
  onSuccess,
  onExit,
  variant = 'primary',
  className = '',
  children,
}: PlaidLinkButtonProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch link token from backend
  const fetchLinkToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/plaid/create-link-token') as any;
      
      // api.post returns the unwrapped body: { success, data: { link_token } }
      const body = response.data ? (response.data.success !== undefined ? response.data : response) : response;
      if (body?.success && (body?.data?.link_token || body?.link_token)) {
        setLinkToken(body.data?.link_token || body.link_token);
      } else {
        throw new Error('Failed to create link token');
      }
    } catch (err: any) {
      console.error('Failed to fetch Plaid link token:', err);
      setError(err.message || 'Failed to initialize bank connection');
      toast.error('Failed to initialize bank connection. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle successful Plaid Link completion
  const handleOnSuccess = useCallback(async (publicToken: string, metadata: any) => {
    setLoading(true);
    
    try {
      console.log('Plaid Link success, exchanging token...', metadata);
      
      // Exchange public token for access token and store accounts
      const response = await api.post('/plaid/exchange-token', {
        public_token: publicToken,
        accounts: metadata.accounts,
        institution: metadata.institution,
      }) as any;
      
      // api.post returns unwrapped body: { success, data: { linked, accounts } }
      const body = response.data ? (response.data.success !== undefined ? response.data : response) : response;
      if (body?.success && body?.data) {
        toast.success(`Successfully linked ${body.data.linked} account(s)!`);
        onSuccess(body.data.accounts);
      } else {
        throw new Error(body?.error || 'Failed to link accounts');
      }
    } catch (err: any) {
      console.error('Failed to exchange Plaid token:', err);
      toast.error(err.message || 'Failed to complete bank connection');
    } finally {
      setLoading(false);
      setLinkToken(null);
    }
  }, [onSuccess]);

  // Handle Plaid Link exit (user closed modal)
  const handleOnExit = useCallback((err: any, metadata: any) => {
    if (err) {
      console.error('Plaid Link error:', err);
      if (err.error_code !== 'USER_CANCELLED') {
        toast.error(err.display_message || 'Bank connection was interrupted');
      }
    }
    
    setLinkToken(null);
    onExit?.();
  }, [onExit]);

  // Initialize Plaid Link
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  });

  // Handle button click
  const handleClick = async () => {
    if (linkToken && ready) {
      open();
    } else {
      await fetchLinkToken();
    }
  };

  // Auto-open when token is ready
  React.useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  // Button styles based on variant
  const baseStyles = 'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantStyles = {
    primary: 'bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg',
    secondary: 'bg-secondary text-white hover:bg-secondary/90',
    outline: 'border-2 border-primary text-primary hover:bg-primary/10',
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading}
        className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Building2 className="w-5 h-5" />
        )}
        {children || (loading ? 'Connecting...' : 'Connect Bank with Plaid')}
      </button>
      
      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}
    </>
  );
}
